import { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Check, ChevronLeft, ChevronRight } from 'lucide-react';
import Footer from '../../components/Footer';
import { fadeInUp, staggerContainer } from '../../hooks/useScrollAnimation';

// Product Card Component with Image Carousel
const ProductCard = ({ product, index }) => {
  const [currentImage, setCurrentImage] = useState(0);
  
  const nextImage = () => {
    setCurrentImage((prev) => (prev + 1) % product.images.length);
  };
  
  const prevImage = () => {
    setCurrentImage((prev) => (prev - 1 + product.images.length) % product.images.length);
  };
  
  return (
    <motion.div
      variants={itemVariants}
      className={`flex flex-col ${
        index % 2 === 1 ? 'lg:flex-row-reverse' : 'lg:flex-row'
      } gap-8 lg:gap-12 items-center`}
    >
      {/* Image Carousel */}
      <motion.div 
        className="w-full lg:w-1/2"
        whileHover={{ scale: 1.02 }}
        transition={{ duration: 0.3 }}
      >
        <div className="aspect-video rounded-2xl overflow-hidden shadow-lg relative group">
          <img
            src={product.images[currentImage]}
            alt={product.title}
            width="800"
            height="450"
            loading="lazy"
            decoding="async"
            className="w-full h-full object-cover transition-transform duration-700"
          />
          
          {/* Carousel Controls */}
          {product.images.length > 1 && (
            <>
              <button
                onClick={prevImage}
                className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-charcoal/50 hover:bg-charcoal/70 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                aria-label="Previous image"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <button
                onClick={nextImage}
                className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-charcoal/50 hover:bg-charcoal/70 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                aria-label="Next image"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
              
              {/* Dots Indicator */}
              <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
                {product.images.map((_, idx) => (
                  <button
                    key={idx}
                    onClick={() => setCurrentImage(idx)}
                    className={`w-2 h-2 rounded-full transition-all ${
                      idx === currentImage ? 'bg-lime w-4' : 'bg-white/70'
                    }`}
                    aria-label={`Go to image ${idx + 1}`}
                  />
                ))}
              </div>
              
              {/* Image Counter */}
              <div className="absolute top-4 right-4 bg-charcoal/50 text-white text-xs px-2 py-1 rounded">
                {currentImage + 1} / {product.images.length}
              </div>
            </>
          )}
        </div>
      </motion.div>

      {/* Content */}
      <motion.div 
        className="w-full lg:w-1/2"
        initial={{ opacity: 0, x: index % 2 === 1 ? -30 : 30 }}
        whileInView={{ opacity: 1, x: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6, delay: 0.2 }}
      >
        <motion.span 
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 0.3 }}
          className="font-mono text-xs uppercase tracking-widest text-charcoal/50 mb-2 block"
        >
          {product.subtitle}
        </motion.span>
        <h2
          className="font-display font-bold text-charcoal mb-4"
          style={{ fontSize: 'clamp(24px, 3vw, 40px)', lineHeight: 1.2 }}
        >
          {product.title}
        </h2>
        <p className="text-charcoal/70 mb-6 leading-relaxed">
          {product.description}
        </p>

        {/* Features */}
        <motion.ul 
          variants={staggerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="space-y-2 mb-8"
        >
          {product.features.map((feature, idx) => (
            <motion.li 
              key={idx} 
              variants={fadeInUp}
              className="flex items-center gap-2 text-charcoal/80"
            >
              <Check className="w-4 h-4 text-lime flex-shrink-0" />
              <span className="text-sm">{feature}</span>
            </motion.li>
          ))}
        </motion.ul>

        <motion.div
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          <Link
            to={`/products/${product.id}`}
            className="bg-lime text-charcoal px-6 py-3 rounded-lg font-medium inline-flex items-center gap-2 hover:bg-lime/90 transition-all duration-300"
          >
            Learn More
            <ArrowRight className="w-4 h-4" />
          </Link>
        </motion.div>
      </motion.div>
    </motion.div>
  );
};

// Animation variants
const itemVariants = {
  hidden: { opacity: 0, y: 40 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, ease: [0.25, 0.1, 0.25, 1] }
  }
};

const Products = () => {
  const products = [
    {
      id: 'racking',
      title: 'Pallet Racking Systems',
      subtitle: 'RACKING THAT HOLDS UP.',
      description: 'Selective, drive-in, cantilever and push-back configurations—engineered for your load, height and throughput. Our racking systems maximize vertical space while ensuring safe, efficient access to your inventory.',
      images: [
        '/racking_aisle.jpg',
        '/images/products/racking.webp',
        '/images/products/warehouse.webp',
      ],
      features: [
        'Load capacity up to 4,000kg per level',
        'Height adjustable in 50mm increments',
        'Powder-coated or galvanized finish',
        'SABS & ISO 9001 certified',
        'Professional installation included',
      ],
    },
    {
      id: 'shelving',
      title: 'Industrial Shelving',
      subtitle: 'SHELVING THAT FITS.',
      description: 'Heavy-duty bays for warehouses, archives, retail backrooms and workshops—adjustable, modular, built to last. From light-duty office shelving to heavy-duty industrial systems.',
      images: [
        '/shelving_bay.jpg',
        '/images/products/shelving.webp',
      ],
      features: [
        'Bolted or boltless options',
        'Steel, chrome, or plastic finishes',
        'Ideal for archives, parts, retail',
        'Mobile shelving systems available',
        'Custom sizing for any space',
      ],
    },
    {
      id: 'mezzanine',
      title: 'Mezzanine Floors',
      subtitle: 'MEZZANINE THAT MULTIPLIES SPACE.',
      description: 'Add a second level without moving. We design, certify and install mezzanines for storage, production or office use. Double your floor space with minimal disruption.',
      images: [
        '/mezzanine_install.jpg',
        '/images/products/mezzanine.webp',
      ],
      features: [
        'Building control approved designs',
        'Load capacity from 300kg to 1,000kg/m²',
        'Staircases, gates & safety rails',
        'Fire-rated materials available',
        'Full project management included',
      ],
    },
    {
      id: 'storage',
      title: 'Lockers & Storage',
      subtitle: 'STORAGE THAT STAYS ORGANIZED.',
      description: 'Secure lockers, cabinets and bin systems to protect inventory, tools and personal items—keyed or digital. Keep your workspace organized and your assets secure.',
      images: [
        '/lockers_storage.jpg',
        '/images/products/locker.webp',
        '/images/products/storage.webp',
      ],
      features: [
        'Steel lockers with ventilation',
        'Digital or key lock options',
        'Small-parts bins & cabinets',
        'Tool storage & shadow boards',
        'Custom configurations available',
      ],
    },
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
        delayChildren: 0.2,
      }
    }
  };

  return (
    <div className="pt-20">
      {/* Header */}
      <section className="w-full bg-charcoal py-20 lg:py-32 overflow-hidden">
        <div className="px-6 lg:px-[7vw]">
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: [0.25, 0.1, 0.25, 1] }}
            className="font-display font-bold text-white mb-4"
            style={{ fontSize: 'clamp(36px, 5vw, 64px)', lineHeight: 1.1 }}
          >
            Our Products
          </motion.h1>
          <motion.p 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.15, ease: [0.25, 0.1, 0.25, 1] }}
            className="text-white/60 max-w-2xl"
          >
            Industrial-grade storage solutions designed for warehouses, retail, and manufacturing facilities across South Africa.
          </motion.p>
        </div>
      </section>

      {/* Products List */}
      <section className="w-full bg-offwhite py-12 lg:py-20">
        <div className="px-6 lg:px-[7vw]">
          <motion.div 
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            className="space-y-16 lg:space-y-24"
          >
            {products.map((product, index) => (
              <ProductCard key={product.id} product={product} index={index} />
            ))}
          </motion.div>
        </div>
      </section>

      {/* CTA */}
      <section className="w-full bg-charcoal py-16 overflow-hidden">
        <div className="px-6 lg:px-[7vw] text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="font-display font-bold text-white text-2xl lg:text-3xl mb-4">
              Not sure what you need?
            </h2>
            <p className="text-white/60 max-w-xl mx-auto mb-6">
              Contact us for a free consultation. We'll help you find the right solution for your space.
            </p>
            <motion.div
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="inline-block"
            >
              <Link
                to="/contact"
                className="bg-lime text-charcoal px-8 py-4 rounded-lg font-medium hover:bg-lime/90 transition-colors inline-block"
              >
                Get a Quote
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Products;
